/**
 * The assessment categories the reports count by, and what to call them.
 *
 * The map had been spelled once in the Dashboard's pie chart and again in the
 * Discipline report's breakdown, so a renamed category showed under two names
 * on two tabs of the same section.
 *
 * @module reports/assessment-categories
 */

import { classifyAssessmentType } from "@/utils/assessments";

import type { IndexedAssessment } from "./types";

const CATEGORY_LABELS: Record<string, string> = {
  continuous: "Continuous",
  invigilated: "Invigilated exam",
  proctored: "Online proctored",
  project: "Project",
  practical: "Practical or lab",
  workBased: "Work-based",
};

/** What to call a category. Unknown ones report themselves. */
export function assessmentCategoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

/** How many assessments fall in each category, commonest first. */
export function countAssessmentCategories(
  assessments: readonly IndexedAssessment[],
): readonly [string, number][] {
  const counts = new Map<string, number>();
  assessments.forEach((ia) => {
    const category = classifyAssessmentType(ia.assessment.type ?? "");
    counts.set(category, (counts.get(category) ?? 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1]);
}
